const buildOwnerParams = (selectedRecipients, ownerContacts, includeAllRecipients = false) => {
  const ALL_RECIPIENTS = 'All Recipients';
  if (selectedRecipients[0]?.value === ALL_RECIPIENTS) {
    if (!includeAllRecipients) {
      return [];
    }
    else {
      return getAllRecipients(ownerContacts);
    };
  }
  let owners = selectedRecipients.reduce((acc, recipient) => {
    const value = recipient.value;
    if (value === 'Organizations') {
      // "Organization" : [{key: "Fax Number", value: "1234567890"}, {key: "Email Attachment", value: "test@test.com"}]
      acc.push(...(ownerContacts.Organization?.map((organization) => organization.value) || []));
    } else if (value.startsWith('Department:')) {
      // "Departments" : { "Department Name" : [{key: "Fax Number", value: "1234567890"}, {key: "Email Attachment", value: "test@test.com"}] }
      const department = value.split('Department: ')[1];
      acc.push(...(ownerContacts.Departments?.[department]?.map((department) => department.value) || []));
    } else if (value.startsWith('User:')) {
      // "Users" : { "User Name" : ["1234567890", "test@test.com"] }
      const user = value.split('User: ')[1];
      acc.push(...(ownerContacts.Users?.[user] || []));
    } else {
      acc.push(value);
    }
    return acc;
  }, []);
  return owners;
};

const getAllRecipients = (ownerContacts) => {
  return [
    ...(ownerContacts.Organization?.map((organization) => organization.value) || []),
    ...Object.values(ownerContacts.Departments || {}).flatMap((department) => department.map((dept) => dept.value)),
    ...Object.values(ownerContacts.Users || {}).flat(),
  ];
};

const downloadCSV = (data, columns) => {
 if (!data || !Array.isArray(data) || data.length === 0) {
    console.error('No data to export');
    alert('No data to export');
    return;
  }

  // Extract headers from columns
  const headers = columns.map(col => col.title);
  
  // Convert data to CSV rows
  const csvRows = data.map(row => {
    return columns.map(col => {
      const value = row[col.dataIndex];
      // Handle null/undefined values
      if (value === null || value === undefined) {
        return '-';
      }
      // Handle boolean values
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      // Handle dates and other values
      return String(value);
    });
  });
  
  // Combine headers and rows
  const csvContent = [headers, ...csvRows]
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n');
  
  // Create and download the file
  const csvContentWithBOM = '\uFEFF' + csvContent; // Add BOM for Excel compatibility
  const blob = new Blob([csvContentWithBOM], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', 'referral_report.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const calculateReportTime = time => {
  if (!time) {
    return '0 hrs';
  }
  
  const days = Math.floor(time / 24);
  const remainingHours = time - days * 24;
  const roundedHours = Math.round(remainingHours);
  
  if (roundedHours === 24) {
    const totalDays = days + 1;
    return totalDays === 1 ? '1 day' : `${totalDays} days`;
  }
  
  if (days === 0) {
    return `${time} hrs`;
  }
  
  if (roundedHours === 0) {
    return days === 1 ? '1 day' : `${days} days`;
  }
  
  return `${days}d ${roundedHours}h`;
};

const compactParams = (params) => {
  const compacted = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    if (Array.isArray(value) && value.length === 0) return;

    compacted[key] = value;
  });

  return compacted;
};

const csrfToken = () => document.getElementsByName('csrf-token')[0]?.content;

const humanizeSnakeCase = (value) => {
  if (value === null || value === undefined || value === '') return '';

  return String(value)
    .split('_')
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Serializes a MAIRA filter object ({ dateRange, selectedVoiceAiNumberIds,
// selectedDepartmentSlugs, sentimentBucket, outcome }) to the API param shape.
// sentiment_bucket / outcome drop out when absent (dashboard filters omit them),
// and compactParams strips empty arrays and blank dates. Pass includeDates: false
// to send a request without a date range (initial dashboard load).
const mairaFilterParams = (filters, { includeDates = true } = {}) => compactParams({
  start_date: includeDates ? filters.dateRange.startDate : null,
  end_date: includeDates ? filters.dateRange.endDate : null,
  voice_ai_number_ids: filters.selectedVoiceAiNumberIds,
  department_slugs: filters.selectedDepartmentSlugs,
  sentiment_bucket: filters.sentimentBucket,
  outcome: filters.outcome,
});

export {
  buildOwnerParams,
  compactParams,
  csrfToken,
  downloadCSV,
  calculateReportTime,
  humanizeSnakeCase,
  mairaFilterParams,
};
